import { detailOf, identityHeaders, type Source } from '../api/client';

/**
 * The credential's six calls, and the two rules every one of them keeps
 * (docs/DECISIONS.md ZD-118 §3).
 *
 * ## A refusal is the API's sentence, verbatim
 *
 * `sds-13a` decided what a person is told — one message per shape, never
 * which half was wrong or whether an email is an account (ZD-117 §7) — and
 * equalised the timing to match. A page that mapped a 401 to a sentence naming
 * the email as the half that was wrong would undo that with a lookup table. So a `Refusal` carries the
 * status and the API's own `{error}` string, and nothing here composes,
 * interprets or adds to it. Where the body carried no message, that is said
 * as such rather than filled in.
 *
 * ## A success is exactly the documented status
 *
 * Every route here answers one status on success — `204` for the four that
 * set or clear the cookie, `202` for a reset request, `200` for `/v1/me` —
 * and this refuses any other. The failure it exists for is silent: a
 * deployment whose `run_worker_first` did not cover `/v1/auth/*` would have
 * the static-asset handler answer `200 text/html` to a sign-in, `fetch` would
 * call that `ok`, and a page reading `ok` would tell a person they were
 * signed in with no cookie set. `UnexpectedAnswerError` is thrown rather than
 * returned because it is a deployment defect and not something a person can
 * act on.
 *
 * ## Same origin, always
 *
 * The workspace's reads may be pointed elsewhere with `?api=`; these may not.
 * A `__Host-` cookie is bound to the origin that set it and travels to no
 * other, so a credential obtained from a different origin would sign nothing
 * in. Every URL here is relative.
 *
 * Nothing here logs, stores, or puts a password or token anywhere but the
 * request body; `credential.test.ts` asserts that of every module in this
 * directory.
 */

export interface Refusal {
  ok: false;
  status: number;
  message: string;
}
export type Outcome<T = null> = { ok: true; value: T } | Refusal;

export class UnexpectedAnswerError extends Error {
  constructor(
    readonly what: string,
    readonly status: number,
    readonly expected: number,
  ) {
    super(
      `${what}: the API answered ${String(status)} where ${String(expected)} was expected. ` +
        `This is a deployment defect rather than something you did; nothing was changed.`,
    );
    this.name = 'UnexpectedAnswerError';
  }
}

/** Who is signed in, as `GET /v1/me` reports it. */
export interface Me {
  user_id: string;
  email: string;
  display_name: string;
  role: string;
  tenant_name: string;
}

async function post(path: string, body: Record<string, string>): Promise<Response> {
  return fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  });
}

/** One outcome from one response, under the two rules above. */
async function settle(response: Response, expected: number, what: string): Promise<Outcome> {
  if (response.status === expected) return { ok: true, value: null };
  if (!response.ok)
    return { ok: false, status: response.status, message: await detailOf(response) };
  throw new UnexpectedAnswerError(what, response.status, expected);
}

export async function signIn(email: string, password: string): Promise<Outcome> {
  return settle(await post('/v1/auth/sign-in', { email, password }), 204, 'signing in');
}

export async function signOut(): Promise<Outcome> {
  const response = await fetch('/v1/auth/sign-out', { method: 'POST', credentials: 'same-origin' });
  return settle(response, 204, 'signing out');
}

export async function acceptInvitation(token: string, password: string): Promise<Outcome> {
  return settle(
    await post('/v1/auth/invitation', { token, password }),
    204,
    'accepting the invitation',
  );
}

/**
 * `202` whatever the email is, by design: the answer says nothing about
 * whether the email is an account, and the page repeats the API's own status
 * sentence rather than composing one that might.
 */
export async function requestReset(email: string): Promise<Outcome<string>> {
  const response = await post('/v1/auth/reset-request', { email });
  if (response.status === 202) {
    const body = (await response.json().catch(() => null)) as { status?: unknown } | null;
    return {
      ok: true,
      value: typeof body?.status === 'string' ? body.status : 'The request was accepted.',
    };
  }
  if (!response.ok)
    return { ok: false, status: response.status, message: await detailOf(response) };
  throw new UnexpectedAnswerError('requesting a reset', response.status, 202);
}

export async function resetPassword(token: string, password: string): Promise<Outcome> {
  return settle(await post('/v1/auth/reset', { token, password }), 204, 'resetting the password');
}

/**
 * The workspace's own identity, read from the record rather than from the URL
 * (ZD-115 §8). Sent with whatever claims the URL stated, as every other
 * workspace read is, because under `dev-header` those are the stand-in's
 * inputs; under a cookie they are claims the record checks.
 */
export async function whoAmI(source: Source): Promise<Outcome<Me>> {
  const base = source.base ?? '';
  const response = await fetch(`${base.replace(/\/+$/u, '')}/v1/me`, {
    headers: identityHeaders(source),
    credentials: 'same-origin',
  });
  if (!response.ok)
    return { ok: false, status: response.status, message: await detailOf(response) };
  if (response.status !== 200)
    throw new UnexpectedAnswerError('reading who is signed in', response.status, 200);
  const body = (await response.json()) as {
    user?: { user_id?: unknown; email?: unknown; display_name?: unknown; role?: unknown };
    tenant?: { name?: unknown };
  };
  const user = body.user;
  if (
    typeof user?.user_id !== 'string' ||
    typeof user.email !== 'string' ||
    typeof user.display_name !== 'string' ||
    typeof user.role !== 'string' ||
    typeof body.tenant?.name !== 'string'
  ) {
    throw new UnexpectedAnswerError('reading who is signed in', response.status, 200);
  }
  return {
    ok: true,
    value: {
      user_id: user.user_id,
      email: user.email,
      display_name: user.display_name,
      role: user.role,
      tenant_name: body.tenant.name,
    },
  };
}
